import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { FileSpreadsheet, UserPlus, KeyRound, CheckCircle2, XCircle } from "lucide-react";

export default function Approvals() {
  const [data, setData] = useState({ templates: [], user_registrations: [], password_resets: [] });
  const [bus, setBus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get("/approvals/pending"),
        api.get("/business-units").catch(() => ({ data: [] })),
      ]);
      setData(a.data);
      setBus(b.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const buName = (id) => bus.find((b) => b.id === id)?.name || "—";

  const runAction = async (key, fn) => {
    setBusy(key);
    try { await fn(); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  const approveTemplate = (t) => runAction(`tpl-${t.id}`, async () => {
    // BU Owner step → /owner-approve. Super Admin step → /super-approve.
    if (t.status === "pending_owner") await api.post(`/templates/${t.id}/owner-approve`);
    else await api.post(`/templates/${t.id}/super-approve`);
    toast.success(`Template "${t.name}" approved`);
  });

  const approveUser = (u) => runAction(`usr-${u.id}`, async () => {
    await api.post(`/users/${u.id}/approve`);
    toast.success(`${u.name} approved`);
  });
  const denyUser = (u) => runAction(`usr-${u.id}`, async () => {
    if (!confirm(`Deny ${u.name}'s account request?`)) throw new Error("cancelled");
    await api.post(`/users/${u.id}/deny`);
    toast.success(`${u.name} denied`);
  });

  const approveReset = (r) => runAction(`pwd-${r.id}`, async () => {
    await api.post(`/password-reset-requests/${r.id}/approve`);
    toast.success("Password reset approved — temp password emailed");
  });
  const denyReset = (r) => runAction(`pwd-${r.id}`, async () => {
    await api.post(`/password-reset-requests/${r.id}/deny`);
    toast.success("Password reset denied");
  });

  const total = (data.templates?.length || 0) + (data.user_registrations?.length || 0) + (data.password_resets?.length || 0);

  return (
    <div>
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Action queue</div>
        <h1 className="text-4xl font-bold text-zinc-900 mt-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          Approvals
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          {loading ? "Loading…" : total === 0 ? "Nothing waiting on you. " : `${total} item${total === 1 ? "" : "s"} need your decision.`}
        </p>
      </div>

      {/* Templates */}
      <Section icon={FileSpreadsheet} title="Templates awaiting sign-off" count={data.templates.length}>
        {data.templates.map((t) => {
          const stepLabel = t.status === "pending_owner" ? "Awaiting BU Owner" : t.status === "pending_super" ? "Awaiting Super Admin" : t.status;
          return (
            <Row key={t.id} testId={`approval-tpl-${t.id}`}
              title={t.name}
              subtitle={<>{buName(t.business_unit_id)} · FY {t.fiscal_year} · {t.rows?.length || 0} rows · <span className="text-amber-700 font-medium">{stepLabel}</span></>}
              primary={{ label: "Approve", onClick: () => approveTemplate(t), busy: busy === `tpl-${t.id}`, testId: `approval-tpl-approve-${t.id}` }}
              secondary={<Link to={`/templates/${t.id}/view`} className="text-xs text-blue-600 hover:underline">View →</Link>}
            />
          );
        })}
      </Section>

      {/* User registrations */}
      <Section icon={UserPlus} title="User registrations" count={data.user_registrations.length}>
        {data.user_registrations.map((u) => (
          <Row key={u.id} testId={`approval-usr-${u.id}`}
            title={u.name}
            subtitle={<>{u.email} · role <code className="text-[10px] bg-zinc-100 px-1 py-0.5 rounded">{u.role}</code></>}
            primary={{ label: "Approve", onClick: () => approveUser(u), busy: busy === `usr-${u.id}`, testId: `approval-usr-approve-${u.id}` }}
            danger={{ label: "Deny", onClick: () => denyUser(u), busy: busy === `usr-${u.id}`, testId: `approval-usr-deny-${u.id}` }}
          />
        ))}
      </Section>

      {/* Password resets */}
      <Section icon={KeyRound} title="Password reset requests" count={data.password_resets.length}>
        {data.password_resets.map((r) => (
          <Row key={r.id} testId={`approval-pwd-${r.id}`}
            title={r.email || r.user_id}
            subtitle={<>Requested {r.requested_at ? new Date(r.requested_at).toLocaleString() : "—"}</>}
            primary={{ label: "Approve", onClick: () => approveReset(r), busy: busy === `pwd-${r.id}`, testId: `approval-pwd-approve-${r.id}` }}
            danger={{ label: "Deny", onClick: () => denyReset(r), busy: busy === `pwd-${r.id}`, testId: `approval-pwd-deny-${r.id}` }}
          />
        ))}
      </Section>
    </div>
  );
}

function Section({ icon: Icon, title, count, children }) {
  return (
    <div className="mb-8">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-zinc-500" strokeWidth={1.75} />
        <h2 className="text-sm font-semibold uppercase tracking-wider text-zinc-700">{title}</h2>
        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border bg-white text-zinc-700 border-zinc-200">{count}</span>
      </div>
      <div className="border border-zinc-200 bg-white divide-y divide-zinc-100">
        {React.Children.count(children) === 0 ? (
          <div className="px-5 py-8 text-center text-zinc-400 text-sm">Nothing pending here.</div>
        ) : children}
      </div>
    </div>
  );
}

function Row({ title, subtitle, primary, danger, secondary, testId }) {
  return (
    <div data-testid={testId} className="px-5 py-4 flex items-center gap-4">
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm">{title}</div>
        <div className="text-xs text-zinc-500 mt-0.5">{subtitle}</div>
      </div>
      <div className="flex items-center gap-2">
        {secondary}
        {danger && (
          <button data-testid={danger.testId} onClick={danger.onClick} disabled={danger.busy}
            className="text-xs px-3 py-1.5 border border-red-200 text-red-700 rounded-sm hover:bg-red-50 disabled:opacity-50 flex items-center gap-1">
            <XCircle className="w-3.5 h-3.5" /> {danger.label}
          </button>
        )}
        {primary && (
          <button data-testid={primary.testId} onClick={primary.onClick} disabled={primary.busy}
            className="text-xs px-3 py-1.5 bg-zinc-900 text-white rounded-sm hover:bg-zinc-800 disabled:opacity-50 flex items-center gap-1">
            <CheckCircle2 className="w-3.5 h-3.5" /> {primary.busy ? "…" : primary.label}
          </button>
        )}
      </div>
    </div>
  );
}
